<script setup lang="ts">
import type {
  FaceDemographicOption,
  FaceDropdownPanelStyle,
  FaceDropdownState,
} from './faceSelectionTypes';

defineProps<{
  options: readonly FaceDemographicOption[];
  panelStyle: FaceDropdownPanelStyle;
  state: FaceDropdownState | null;
}>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<{
  close: [];
  select: [value: string];
}>();
</script>

<template>
  <Teleport to="body">
    <div
      v-if="state"
      class="FacesDropdown__backdrop"
      data-testid="faces-dropdown-backdrop"
      @click="emit('close')"
    >
      <div
        class="FacesDropdown__panel sentry-block"
        data-testid="faces-dropdown"
        :style="panelStyle"
        @click.stop
      >
        <div
          v-for="option in options"
          :key="option.value"
          class="FacesDropdown__option"
          data-testid="faces-dropdown-option"
          :class="{
            'FacesDropdown__option--active':
              option.value === state.currentValue,
          }"
          @click="emit('select', option.value)"
        >
          {{ option.label }}
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.FacesDropdown__backdrop {
  position: fixed;
  inset: 0;
  z-index: 99998;
}

.FacesDropdown__panel {
  background: var(--Kiosk--button_background_color, #fff);
  color: var(--Kiosk--button_font_color, #000);
  border-radius: var(--Kiosk--button_rounding, 0.5em);
  box-shadow: 0 0.4rem 1.5rem rgba(0, 0, 0, 0.35);
  overflow: hidden;
  max-height: 50vh;
  overflow-y: auto;
}

.FacesDropdown__option {
  padding: var(--faces-dropdown-option-padding, 0.7rem 1rem);
  font-size: var(--faces-dropdown-option-font-size, 1.1rem);
  cursor: pointer;
  font-family: var(--Kiosk--font_headers_url, var(--Global--font));
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  letter-spacing: 0.03rem;

  &:active {
    background: rgba(0, 0, 0, 0.12);
  }

  &--active {
    font-weight: bold;
    background: rgba(0, 0, 0, 0.06);
  }

  & + & {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
}

</style>
